"use client"

import { VolumeMax, VolumeX, X } from "@untitledui/icons"
import { AnimatePresence, motion } from "motion/react"
import type { ReactNode } from "react"
import { useEffect } from "react"

import {
  ContentPlayer,
  type ContentPlayerAspect,
} from "@/components/player/content-player"
import { usePlayerState } from "@/hooks/use-player-state"
import { coordinator } from "@/lib/player/coordinator"
import type { PlayableItem } from "@/lib/player/playable-item"
import { cn } from "@/lib/utils"

export interface InlinePreviewShellProps {
  item: PlayableItem
  /**
   * Rendered beside the poster in compact mode and below the live player in
   * expanded mode. Typically contains contributor avatar + name + timestamp
   * + notes — i.e. everything that is not the content itself.
   */
  metadata: ReactNode
  /** Override the live-mode aspect. Defaults per content kind. */
  aspect?: ContentPlayerAspect
  className?: string
}

/**
 * Inline preview surface for player content. Explicitly NOT a modal — no
 * backdrop, no focus trap, no route change. The preview expands in place,
 * collapsing back to a poster the moment another preview is activated or
 * the user hits Escape / the close chip.
 *
 * Used inside the brief suggestion feed so visitors can actually watch a
 * suggestion before adding their own, without leaving the brief.
 *
 * State: `isLive` is derived from the global coordinator's `activeId`.
 * There is no local mirror — a single source of truth kills the race where
 * a coordinator glitch (e.g. a spurious StrictMode cleanup) would collapse
 * the live view.
 */
export function InlinePreviewShell({
  item,
  metadata,
  aspect,
  className,
}: InlinePreviewShellProps) {
  const { activeId, isMuted } = usePlayerState()
  const isLive = activeId === item.id

  // Escape collapses the active preview. Listener lives on `document` rather
  // than the surface container because the CompactView button unmounts on
  // click (focus returns to <body>) and iframe load often takes focus further
  // out of reach. The coordinator's at-most-one-active invariant is what
  // keeps this safe from cross-shell collisions: only the shell whose
  // `item.id` matches `activeId` wires this effect, so a single Esc collapses
  // exactly the playing preview.
  useEffect(() => {
    if (!isLive) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") releaseActive(item.id)
    }
    document.addEventListener("keydown", onKey)
    return () => document.removeEventListener("keydown", onKey)
  }, [isLive, item.id])

  // Auto-collapse when the active item fires "ended". Photo previews need
  // this so the 6-second timer in ContentPlayer.LivePhoto actually has an
  // effect on this surface; the immersive feed handles its own subscription
  // for auto-advance. Now that onEnded is coordinator-only (no prop), every
  // shell that wants to react to end-of-track subscribes here.
  useEffect(() => {
    if (!isLive) return
    return coordinator.onEnded(item.id, () => releaseActive(item.id))
  }, [isLive, item.id])

  const activate = () => coordinator.setActive(item.id)
  const deactivate = () => releaseActive(item.id)

  return (
    <div className={cn(className)}>
      <AnimatePresence initial={false} mode="popLayout">
        {isLive ? (
          <ExpandedView
            key="live"
            item={item}
            metadata={metadata}
            aspect={aspect ?? liveAspectFor(item)}
            isMuted={isMuted}
            onToggleMute={() => coordinator.toggleMuted()}
            onDeactivate={deactivate}
          />
        ) : (
          <CompactView
            key="poster"
            item={item}
            metadata={metadata}
            onActivate={activate}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

/**
 * Release the active slot iff we still own it. Guards against a race where
 * another surface claimed active between our re-render and our click.
 */
function releaseActive(id: string): void {
  if (coordinator.getSnapshot().activeId === id) {
    coordinator.setActive(null)
  }
}

/* ----------------------------------------------------------------- compact */

interface CompactViewProps {
  item: PlayableItem
  metadata: ReactNode
  onActivate: () => void
}

function CompactView({ item, metadata, onActivate }: CompactViewProps) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4"
    >
      <div
        className={cn(
          "shrink-0 overflow-hidden rounded-xl ring-1 ring-black/5",
          COMPACT_WIDTH[item.kind],
        )}
      >
        <ContentPlayer
          item={item}
          mode="poster"
          aspect={compactAspectFor(item.kind)}
          onRequestLive={onActivate}
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-1">{metadata}</div>
    </motion.div>
  )
}

/* ---------------------------------------------------------------- expanded */

interface ExpandedViewProps {
  item: PlayableItem
  metadata: ReactNode
  aspect: ContentPlayerAspect
  isMuted: boolean
  onToggleMute: () => void
  onDeactivate: () => void
}

function ExpandedView({
  item,
  metadata,
  aspect,
  isMuted,
  onToggleMute,
  onDeactivate,
}: ExpandedViewProps) {
  const showMuteChip = item.kind === "video" || item.kind === "audio"
  return (
    <motion.div
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -4 }}
      transition={{ duration: 0.2 }}
      className="flex flex-col gap-3"
    >
      <div className="relative overflow-hidden rounded-xl ring-1 ring-black/5">
        <ContentPlayer
          item={item}
          mode="live"
          active
          muted={isMuted}
          aspect={aspect}
        />
        <div className="pointer-events-none absolute top-2 right-2 flex gap-1">
          {showMuteChip && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onToggleMute()
              }}
              className="pointer-events-auto inline-flex size-8 cursor-pointer items-center justify-center rounded-full bg-black/60 text-white shadow-lg backdrop-blur-sm hover:bg-black/80"
              aria-label={isMuted ? "Unmute" : "Mute"}
            >
              {isMuted ? (
                <VolumeX className="size-4" />
              ) : (
                <VolumeMax className="size-4" />
              )}
            </button>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onDeactivate()
            }}
            className="pointer-events-auto inline-flex size-8 cursor-pointer items-center justify-center rounded-full bg-black/60 text-white shadow-lg backdrop-blur-sm hover:bg-black/80"
            aria-label="Collapse preview"
          >
            <X className="size-4" />
          </button>
        </div>
      </div>
      <div className="flex min-w-0 flex-col gap-1">{metadata}</div>
    </motion.div>
  )
}

/* ------------------------------------------------------------------ sizing */

/**
 * Compact poster width per kind. Tuned for cinematic-feeling tiles in the
 * brief suggestion row without overwhelming the metadata column. Heights
 * are derived from the aspect ratio so all suggestions in a single prompt
 * (same kind) share a consistent silhouette.
 */
const COMPACT_WIDTH: Record<PlayableItem["kind"], string> = {
  video: "w-56 sm:w-64",
  audio: "w-40 sm:w-44",
  photo: "w-44 sm:w-48",
  article: "w-44 sm:w-48",
}

function compactAspectFor(kind: PlayableItem["kind"]): ContentPlayerAspect {
  switch (kind) {
    case "video":
      return "16/9"
    case "audio":
      return "1/1"
    case "photo":
      return "4/5"
    case "article":
      return "4/5"
  }
}

/**
 * Expanded-mode aspect. Video/audio letterbox 16/9 so iframe embeds look
 * balanced in a column-width frame; article stays 4/5 so there is enough
 * vertical room to read without a second scroll. Content-level intent
 * (`item.aspect` from `VideoBody.aspect_label`) wins over the per-kind
 * default when present and supported — a 9:16 vertical video should
 * render 9:16 inline, not letterboxed 16:9.
 */
const KNOWN_ASPECTS: readonly ContentPlayerAspect[] = [
  "9/16",
  "16/9",
  "1/1",
  "4/5",
  "4/3",
]

function liveAspectFor(item: PlayableItem): ContentPlayerAspect {
  if (
    item.aspect &&
    (KNOWN_ASPECTS as readonly string[]).includes(item.aspect)
  ) {
    return item.aspect
  }
  switch (item.kind) {
    case "video":
      return "16/9"
    case "audio":
      return "16/9"
    case "photo":
      return "4/5"
    case "article":
      return "4/5"
  }
}
